import { useState } from "react"
import {
  X,
  ChevronDown,
  ChevronRight,
  CheckCircle,
  XCircle,
  Clock,
  PlayCircle,
  FileText,
  Award,
  Video,
  BookOpen,
  RotateCcw,
  Loader2,
} from "lucide-react"
import { useLearnerCourseProgress } from "../../../hooks/useStats"
import { api } from "../../../utils/apiComponent/apiClient"
import { endpoints } from "../../../utils/endpoints"
import { toastUtils } from "../../../utils/toast"

const formatDuration = (seconds) => {
  if (!seconds) return "0s"
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = seconds % 60
  if (h > 0) return `${h}h ${m}min`
  if (m > 0) return `${m}min ${s}s`
  return `${s}s`
}

const formatDateTime = (d) => {
  if (!d) return "—"
  try {
    return new Date(d).toLocaleString("fr-FR")
  } catch {
    return "—"
  }
}

const STATUS_ICON = {
  COMPLETED: { icon: CheckCircle, color: "text-green-600" },
  IN_PROGRESS: { icon: PlayCircle, color: "text-blue-600" },
  NOT_STARTED: { icon: Clock, color: "text-gray-400" },
}

const BLOCK_TYPE_ICON = {
  VIDEO: Video,
  TEXT: FileText,
  QUIZ: Award,
}

const ProgressBar = ({ value, color = "#203a8a" }) => {
  const pct = Math.min(100, Math.max(0, value || 0))
  return (
    <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden">
      <div
        className="h-1.5 rounded-full transition-all"
        style={{ width: `${pct}%`, backgroundColor: color }}
      />
    </div>
  )
}

const StatusIcon = ({ status, size = 16 }) => {
  const cfg = STATUS_ICON[status] || STATUS_ICON.NOT_STARTED
  const Icon = cfg.icon
  return <Icon size={size} className={cfg.color} />
}

export default function LearnerCourseProgressModal({
  isOpen,
  userId,
  courseId,
  onClose,
}) {
  const [expandedModules, setExpandedModules] = useState({})
  const [expandedLessons, setExpandedLessons] = useState({})
  const [resettingQuizId, setResettingQuizId] = useState(null)
  const [resettingCourse, setResettingCourse] = useState(false)
  const [keepCompleted, setKeepCompleted] = useState(false)

  const { data, loading, error, refetch } = useLearnerCourseProgress(
    isOpen ? userId : null,
    isOpen ? courseId : null
  )

  const handleResetQuiz = async (quizId, moduleTitle) => {
    const msg = keepCompleted
      ? `Réinitialiser uniquement les tentatives en cours du quiz du module « ${moduleTitle} » ?`
      : `Réinitialiser TOUTES les tentatives (y compris terminées) du quiz du module « ${moduleTitle} » ?`
    if (!window.confirm(msg)) return
    setResettingQuizId(quizId)
    try {
      const response = await api.post(
        endpoints.resetQuizAttempts(quizId, userId),
        { keepCompleted }
      )
      const count = response?.data?.resetCount ?? 0
      toastUtils.success(
        `${count} tentative(s) réinitialisée(s) pour le module « ${moduleTitle} »`
      )
      refetch()
    } catch (err) {
      toastUtils.error(err?.message || "Erreur lors de la réinitialisation")
    } finally {
      setResettingQuizId(null)
    }
  }

  const handleResetAllCourseQuizzes = async () => {
    const msg = keepCompleted
      ? "Réinitialiser uniquement les tentatives en cours de TOUS les quiz de ce cours ?"
      : "Réinitialiser TOUTES les tentatives de TOUS les quiz de ce cours ?"
    if (!window.confirm(msg)) return
    setResettingCourse(true)
    try {
      const response = await api.post(
        endpoints.resetCourseQuizAttempts(courseId, userId),
        { keepCompleted }
      )
      const count = response?.data?.resetCount ?? 0
      const nbQuizzes = response?.data?.quizzesReset?.length ?? 0
      toastUtils.success(
        `${count} tentative(s) réinitialisée(s) sur ${nbQuizzes} quiz`
      )
      refetch()
    } catch (err) {
      toastUtils.error(err?.message || "Erreur lors de la réinitialisation")
    } finally {
      setResettingCourse(false)
    }
  }

  if (!isOpen) return null

  const toggleModule = (id) =>
    setExpandedModules((prev) => ({ ...prev, [id]: !prev[id] }))
  const toggleLesson = (id) =>
    setExpandedLessons((prev) => ({ ...prev, [id]: !prev[id] }))

  const user = data?.user
  const course = data?.course
  const enrollment = data?.enrollment
  const progress = data?.progress
  const certificate = data?.certificate
  const modules = data?.modules || []

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[95vh] flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 p-6 border-b border-gray-200">
          <div className="min-w-0">
            <p className="text-xs text-gray-500 uppercase tracking-wide">
              Progression détaillée
            </p>
            <h2 className="text-xl font-bold text-[#203a8a] truncate">
              {loading ? "Chargement…" : course?.title || "Cours"}
            </h2>
            {user && (
              <p className="text-sm text-gray-600 truncate">
                {user.name} · {user.email}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg flex-shrink-0"
          >
            <X size={20} />
          </button>
        </div>

        {/* Summary */}
        {!loading && data && (
          <div className="p-6 border-b border-gray-200 bg-gray-50">
            {/* Reset controls */}
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3 p-3 bg-white border border-yellow-200 rounded-lg">
              <div className="flex items-center gap-3">
                <Award size={18} className="text-yellow-600" />
                <div>
                  <p className="text-sm font-semibold text-gray-800">
                    Réinitialisation des quiz
                  </p>
                  <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer mt-1">
                    <input
                      type="checkbox"
                      checked={keepCompleted}
                      onChange={(e) => setKeepCompleted(e.target.checked)}
                      className="rounded"
                    />
                    Conserver les tentatives déjà terminées
                  </label>
                </div>
              </div>
              <button
                onClick={handleResetAllCourseQuizzes}
                disabled={resettingCourse}
                className="flex items-center gap-2 px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white text-sm font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {resettingCourse ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <RotateCcw size={14} />
                )}
                Réinitialiser tous les quiz
              </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div>
                <p className="text-xs text-gray-500">Progression globale</p>
                <p className="text-2xl font-bold text-[#203a8a]">
                  {progress?.progress ?? 0}%
                </p>
                <ProgressBar value={progress?.progress} />
              </div>
              <div>
                <p className="text-xs text-gray-500">Leçons complétées</p>
                <p className="text-2xl font-bold text-gray-900">
                  {progress?.completedLessons ?? 0}
                  <span className="text-sm text-gray-500 font-normal">
                    /{progress?.totalLessons ?? 0}
                  </span>
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Statut</p>
                <p className="text-sm font-semibold">
                  {enrollment?.status || "—"}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Inscrit le{" "}
                  {enrollment?.enrolledAt
                    ? new Date(enrollment.enrolledAt).toLocaleDateString("fr-FR")
                    : "—"}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Dernier accès</p>
                <p className="text-sm text-gray-700">
                  {formatDateTime(progress?.lastAccessedAt)}
                </p>
                {certificate && (
                  <div className="flex items-center gap-1 text-xs text-yellow-700 mt-1">
                    <Award size={12} />
                    Certifié
                  </div>
                )}
              </div>
            </div>
            {enrollment?.accessBlockedAt && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
                <strong>Accès bloqué</strong> — {enrollment.accessBlockedReason}
              </div>
            )}
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading && (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#203a8a]"></div>
            </div>
          )}

          {error && !loading && (
            <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
              <p className="font-semibold">Erreur</p>
              <p className="text-sm">{error}</p>
            </div>
          )}

          {!loading && !error && modules.length === 0 && (
            <div className="text-center py-16 text-gray-500 text-sm">
              Aucun module disponible
            </div>
          )}

          {!loading && !error && modules.length > 0 && (
            <div className="space-y-3">
              {modules.map((mod) => {
                const isModuleExpanded = expandedModules[mod.id] ?? true
                return (
                  <div
                    key={mod.id}
                    className="border border-gray-200 rounded-xl overflow-hidden"
                  >
                    {/* Module header */}
                    <button
                      onClick={() => toggleModule(mod.id)}
                      className="w-full flex items-center justify-between gap-3 p-4 bg-gray-50 hover:bg-gray-100"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        {isModuleExpanded ? (
                          <ChevronDown size={18} className="flex-shrink-0" />
                        ) : (
                          <ChevronRight size={18} className="flex-shrink-0" />
                        )}
                        <BookOpen size={18} className="text-[#203a8a] flex-shrink-0" />
                        <div className="text-left min-w-0">
                          <p className="font-semibold text-gray-900 truncate">
                            Module {mod.order} · {mod.title}
                          </p>
                          <p className="text-xs text-gray-500">
                            {mod.completedLessons}/{mod.totalLessons} leçons ·{" "}
                            {formatDuration(mod.duration)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <div className="text-right">
                          <p className="text-sm font-semibold text-[#203a8a]">
                            {mod.progress}%
                          </p>
                          <div className="w-20">
                            <ProgressBar value={mod.progress} />
                          </div>
                        </div>
                      </div>
                    </button>

                    {/* Module body */}
                    {isModuleExpanded && (
                      <div className="p-4 space-y-2 bg-white">
                        {/* Lessons */}
                        {mod.lessons?.map((lesson) => {
                          const isLessonExpanded = expandedLessons[lesson.id]
                          return (
                            <div
                              key={lesson.id}
                              className="border border-gray-100 rounded-lg"
                            >
                              <button
                                onClick={() => toggleLesson(lesson.id)}
                                className="w-full flex items-center justify-between gap-3 p-3 hover:bg-gray-50"
                              >
                                <div className="flex items-center gap-2 min-w-0">
                                  {isLessonExpanded ? (
                                    <ChevronDown
                                      size={14}
                                      className="flex-shrink-0 text-gray-400"
                                    />
                                  ) : (
                                    <ChevronRight
                                      size={14}
                                      className="flex-shrink-0 text-gray-400"
                                    />
                                  )}
                                  <StatusIcon status={lesson.status} />
                                  <div className="text-left min-w-0">
                                    <p className="text-sm font-medium text-gray-900 truncate">
                                      {lesson.order}. {lesson.title}
                                    </p>
                                    <p className="text-xs text-gray-500">
                                      {lesson.completedBlocks}/{lesson.totalBlocks}{" "}
                                      blocs · {formatDuration(lesson.duration)}
                                    </p>
                                  </div>
                                </div>
                                <span className="text-xs font-semibold text-gray-600 flex-shrink-0">
                                  {lesson.progress}%
                                </span>
                              </button>

                              {/* Blocks */}
                              {isLessonExpanded && lesson.blocks?.length > 0 && (
                                <div className="pl-8 pr-3 pb-3 space-y-1">
                                  {lesson.blocks.map((block) => {
                                    const BlockIcon =
                                      BLOCK_TYPE_ICON[block.type] || FileText
                                    return (
                                      <div
                                        key={block.id}
                                        className="flex items-center justify-between gap-2 py-2 px-2 text-xs border-l-2 border-gray-200 hover:bg-gray-50 rounded"
                                      >
                                        <div className="flex items-center gap-2 min-w-0">
                                          <StatusIcon
                                            status={block.status}
                                            size={12}
                                          />
                                          <BlockIcon
                                            size={12}
                                            className="text-gray-400 flex-shrink-0"
                                          />
                                          <span className="truncate">
                                            {block.order}. {block.title}
                                          </span>
                                        </div>
                                        <div className="flex items-center gap-3 flex-shrink-0 text-gray-500">
                                          <span>{formatDuration(block.timeSpent)}</span>
                                          {block.score !== null &&
                                            block.score !== undefined && (
                                              <span className="font-semibold">
                                                {block.score}
                                              </span>
                                            )}
                                          <span className="text-[10px] uppercase">
                                            {block.type}
                                          </span>
                                        </div>
                                      </div>
                                    )
                                  })}
                                </div>
                              )}
                            </div>
                          )
                        })}

                        {/* Quiz */}
                        {mod.quiz && (
                          <div className={`mt-3 p-3 border rounded-lg ${mod.quiz.passed ? 'border-green-200 bg-green-50' : 'border-yellow-200 bg-yellow-50'}`}>
                            <div className="flex items-center justify-between gap-3 mb-2">
                              <div className="flex items-center gap-2 min-w-0">
                                <Award
                                  size={16}
                                  className={mod.quiz.passed ? 'text-green-600' : 'text-yellow-600'} flex-shrink-0
                                />
                                <p className="font-semibold text-sm text-gray-900 truncate">
                                  {mod.quiz.title}
                                </p>
                                {mod.quiz.passed && (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                                    <CheckCircle size={10} /> Validé
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                {!mod.quiz.passed && (
                                  <span className="inline-flex items-center gap-1 text-xs text-red-700">
                                    <XCircle size={12} /> Non réussi
                                  </span>
                                )}
                                <button
                                  onClick={() =>
                                    handleResetQuiz(mod.quiz.id, mod.title)
                                  }
                                  disabled={resettingQuizId === mod.quiz.id || mod.quiz.passed}
                                  title={mod.quiz.passed ? "Quiz validé - impossible de réinitialiser" : "Réinitialiser les tentatives"}
                                  className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded disabled:opacity-50 disabled:cursor-not-allowed ${mod.quiz.passed ? 'bg-gray-100 border border-gray-300 text-gray-400' : 'bg-white border border-yellow-300 text-yellow-700 hover:bg-yellow-100'}`}
                                >
                                  {resettingQuizId === mod.quiz.id ? (
                                    <Loader2 size={12} className="animate-spin" />
                                  ) : (
                                    <RotateCcw size={12} />
                                  )}
                                  Reset
                                </button>
                              </div>
                            </div>
                            <div className="text-xs text-gray-600 mb-2">
                              Score min : {mod.quiz.passingScore} · Tentatives :{" "}
                              {mod.quiz.totalAttempts}/{mod.quiz.maxAttempts} ·
                              Meilleur score : {mod.quiz.bestScore}
                            </div>
                            {mod.quiz.attempts?.length > 0 && (
                              <div className="space-y-1 mt-2">
                                {mod.quiz.attempts.map((a) => (
                                  <div
                                    key={a.id}
                                    className="flex items-center justify-between text-xs bg-white rounded px-2 py-1"
                                  >
                                    <span className="text-gray-600">
                                      {formatDateTime(a.completedAt || a.startedAt)}
                                    </span>
                                    <div className="flex items-center gap-3">
                                      <span>{formatDuration(a.timeSpent)}</span>
                                      <span
                                        className={`font-semibold ${
                                          a.isPassed
                                            ? "text-green-700"
                                            : "text-red-700"
                                        }`}
                                      >
                                        {a.score}/{a.maxScore}
                                      </span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
